(ns superadmin.users.table.view
  (:require ["@mui/material/Autocomplete$default" :as Autocomplete]
            ["@mui/material/Button$default" :as Button]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/IconButton$default" :as IconButton]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/TextField$default" :as TextField]
            ["@mui/x-data-grid-premium" :refer [DataGridPremium]]
            ["react-use/lib/useDebounce$default" :as useDebounce]
            [cljs-bean.core :refer [->clj ->js]]
            [clojure.string :as str]
            [common.queries :refer [use-query]]
            [common.resource.query.agency :as agency-queries]
            [common.util :refer [spread-props]]
            [common.widgets.data-grid :as dg]
            [common.widgets.page :as page]
            [re-frame.core :as rf]
            [reitit.frontend.easy :as r.easy]
            [superadmin.users.create.ocp]
            [superadmin.users.queries :as q]
            [uix.core :as uix :refer [$ defui]]))

;; Button component for navigating to user editor
(defui edit-user-button [{:keys [^js params]}]
  (let [row (->clj (.-row params))
        user-id (:id row)
        handle-click (uix/use-callback
                      (fn [^js e]
                        (.stopPropagation e)
                        (let [ctrl-or-cmd (or (.-ctrlKey e) (.-metaKey e))]
                          (if ctrl-or-cmd
                            ;; Open in new tab
                            (let [url (r.easy/href :superadmin/user-detail {:user-id (str user-id)})]
                              (.open js/window url "_blank"))
                            ;; Navigate in same tab
                            (r.easy/push-state :superadmin/user-detail {:user-id (str user-id)}))))
                      [user-id])]
    ($ IconButton
       {:onClick handle-click
        :size "small"
        :sx #js {:color "grey.10"}
        :data-testid "edit-user-button"}
       ($ Icon "edit"))))

;; Button component for opening user view OCP
(defui view-user-button [{:keys [^js params]}]
  (let [row (->clj (.-row params))
        user-id (:id row)
        handle-click (uix/use-callback
                      (fn [^js e]
                        (.stopPropagation e)
                        (rf/dispatch [:ocp/open {:id :superadmin/user-details
                                                 :props {:user-id user-id
                                                         :user row}}]))
                      [user-id row])]
    ($ IconButton
       {:onClick handle-click
        :size "small"
        :sx #js {:color "grey.10"}
        :data-testid "view-user-button"}
       ($ Icon "visibility"))))

(def ^:private columns
  #js [#js {:field "name"
            :headerName "Name"
            :type "string"
            :minWidth 200
            :flex 2
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (:name row))}
       #js {:field "email"
            :headerName "Email"
            :type "string"
            :minWidth 250
            :flex 3
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (:email row))}
       #js {:field "agencies"
            :headerName "Agencies"
            :type "string"
            :minWidth 200
            :flex 2
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (->> (or (:grants row) [])
                                (mapcat (fn [grant]
                                          (let [scope (:scope grant)]
                                            (cond
                                              (= (:scope-type grant) "agency")
                                              [(get-in scope [:agency :name])]
                                              (= (:scope-type grant) "advertiser")
                                              (when-let [agency-name (get-in scope [:agency :name])]
                                                [agency-name])
                                              :else []))))
                                (remove nil?)
                                distinct
                                (str/join ", ")))}
       #js {:field "advertisers"
            :headerName "Advertisers"
            :type "string"
            :minWidth 200
            :flex 2
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (->> (or (:grants row) [])
                                (filter #(= (:scope-type %) "advertiser"))
                                (map #(get-in % [:scope :advertiser :name]))
                                (remove nil?)
                                distinct
                                (str/join ", ")))}
       #js {:field "grants-count"
            :headerName "# Grants"
            :type "number"
            :minWidth 100
            :flex 1
            :headerAlign "right"
            :align "right"
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (or (:grantsCount row) 0))}
       #js {:field "actions"
            :headerName ""
            :type "actions"
            :width 120
            :sortable false
            :filterable false
            :disableColumnMenu true
            :valueGetter (fn [_value _row ^js _column ^js _apiRef])
            :renderCell (fn [^js params]
                          ($ Stack {:direction "row" :spacing 0.5}
                             ($ view-user-button {:params params})
                             ($ edit-user-button {:params params})))}])

(defn- get-row-id [row]
  (or (:id row) (rand-int 1000)))

(defui agency-filter-toolbar [{:keys [on-change value]}]
  (let [{:keys [data isPending]} (use-query (agency-queries/superadmin-agencies-query))
        options (uix/use-memo
                 (fn []
                   (map (fn [agency]
                          {:label (:name agency)
                           :value (:id agency)})
                        (or data [])))
                 [data])]
    ($ Autocomplete
       {:options (clj->js options)
        :value (if value (:label value) "")
        :loading isPending
        :onChange (fn [_ value _]
                    (on-change (if value (->clj value) nil)))
        :size "small"
        :sx #js {:minWidth "200px"}
        :renderInput (fn [^js props]
                       ($ TextField
                          (spread-props props {:placeholder "Filter by Agency..."
                                               :variant "outlined"})))})))

(defui advertiser-filter-toolbar [{:keys [on-change value selected-agency-id]}]
  (let [{:keys [data isPending]} (use-query (q/agency-advertisers-query selected-agency-id))
        options (uix/use-memo
                 (fn []
                   (map (fn [advertiser]
                          {:label (:name advertiser)
                           :value (:id advertiser)})
                        (or data [])))
                 [data])]
    ($ Autocomplete
       {:options (clj->js options)
        :value (if value (:label value) "")
        :loading isPending
        :onChange (fn [_ value _]
                    (on-change (if value (->clj value) nil)))
        :size "small"
        :sx #js {:minWidth "200px"}
        :renderInput (fn [^js props]
                       ($ TextField
                          (spread-props props {:placeholder "Filter by advertiser..."
                                               :variant "outlined"})))})))

(defui extended-toolbar
  [{:keys [on-agency-change on-advertiser-change
           agency-value advertiser-value]}]
  ($ Stack
     {:direction "row"
      :gap 1
      :alignItems "center"}
     ($ agency-filter-toolbar {:on-change on-agency-change :value agency-value})
     (when agency-value
       ($ advertiser-filter-toolbar {:on-change on-advertiser-change
                                     :value advertiser-value
                                     :selected-agency-id (:value agency-value)}))))

(defui user-data-grid []
  (let [;; Server-side pagination and filtering state
        [pagination-model set-pagination-model] (uix/use-state {:page 0 :pageSize 25})
        [filter-model set-filter-model] (uix/use-state {:items []})
        [agency-filter set-agency-filter] (uix/use-state nil)
        [debounced-query-params set-debounced-query-params] (uix/use-state nil)
        [advertiser-filter set-advertiser-filter] (uix/use-state nil)
        on-agency-change (uix/use-callback
                          (fn [value]
                            (set-advertiser-filter nil) ; clear advertiser on agency selection
                            (set-agency-filter value)
                            (set-pagination-model (fn [current] (assoc current :page 0))))
                          [])
        on-advertiser-change (uix/use-callback
                              (fn [value]
                                (set-advertiser-filter value)
                                (set-pagination-model (fn [current] (assoc current :page 0))))
                              [])
        query-params (let [page (inc (:page pagination-model)) ; API is 1-indexed
                           size (:pageSize pagination-model)
                           agency-id (:value agency-filter)
                           advertiser-id (:value advertiser-filter)
                           raw-search (-> filter-model ->clj :quickFilterValues first (or ""))
                           search (when (and raw-search (>= (count raw-search) 3))
                                    raw-search)
                           filters (->> (:items filter-model)
                                        (map (fn [{:keys [field operator value]}]
                                               {:field field :operator operator :value value}))
                                        (not-empty))]
                       (cond-> {:page page :pagesize size}
                         agency-id (assoc :agency-id agency-id)
                         (and agency-id advertiser-id) (assoc :advertiser-id advertiser-id)
                         search (assoc :search search)
                         filters (assoc :filters filters)))
        {:keys [data isPending]} (use-query (q/users-query debounced-query-params #_query-params))

        rows (or (:items data) [])
        total-count (or (-> data :meta :total) 0)

        custom-part-of-toolbar (uix/use-memo
                                (fn []
                                  (uix/as-react
                                   (fn []
                                     ($ extended-toolbar {:on-agency-change on-agency-change
                                                          :agency-value agency-filter
                                                          :on-advertiser-change on-advertiser-change
                                                          :advertiser-value advertiser-filter}))))
                                [on-agency-change
                                 agency-filter
                                 on-advertiser-change advertiser-filter])

        ;; Custom toolbar with agency and advertiser filters
        custom-toolbar (uix/use-memo
                        (fn []
                          (uix/as-react
                           (fn []
                             ($ dg/grid-toolbar
                                {:sx #js {:justifyContent "flex-start"}}
                                ($ custom-part-of-toolbar)))))
                        [custom-part-of-toolbar])

        handle-pagination-model-change (uix/use-callback
                                        (fn [^js new-model]
                                          (set-pagination-model (->clj new-model)))
                                        [])

        handle-filter-model-change (uix/use-callback
                                    (fn [^js new-model]
                                      (set-filter-model (->clj new-model)))
                                    [])]

    (useDebounce (fn []
                   (set-debounced-query-params query-params))
                 600
                 #js [query-params])

    ($ DataGridPremium
       {:columns columns
        :loading isPending

        :rows (to-array rows)
        :rowCount total-count
        :getRowId get-row-id

        :disableRowSelectionOnClick true

        :pagination true
        :paginationMode "server"
        :paginationModel (->js pagination-model)
        :onPaginationModelChange handle-pagination-model-change
        :pageSizeOptions #js [25 50 100]

        :filterMode "server"
        :filterModel (->js filter-model)
        :onFilterModelChange handle-filter-model-change

        :slots #js {:toolbar custom-toolbar}
        :slotProps #js {:baseTextField dg/grid-toolbar-outlined-input}

        :data-testid "table-users"

        :noWrap true
        :sx #js {:maxHeight "70rem"}})))

(defui users-page []
  ($ page/page
     ($ page/page-header {:title "User Management"
                          :buttons ($ Stack {:direction "row" :spacing 1}
                                      ($ Button
                                         {:variant "outlined"
                                          :onClick #(rf/dispatch [:ocp/open {:id :superadmin/user-create}])
                                          :data-testid "create-user-button"}
                                         "Create User"))})
     ($ page/content-padding {:sx #js {:overflow "hidden"}}
        ($ user-data-grid))))
